import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const DLN_FREE_GIFT_VARIANT_ID = 59016977875276;

/**
 * A custom element that displays a cart icon.
 *
 * DLN:
 * The free gift is a real Shopify cart item, but should never count
 * towards the customer-facing cart quantity.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10) || 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);

    this.ensureCartBubbleIsCorrect();

    // DLN: Always get the real cart state and exclude the free gift.
    this.refreshDLNCartCount(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Returns the customer-facing cart quantity.
   * The DLN free gift does not count.
   *
   * @param {object} cart
   * @returns {number}
   */
  getDLNCartCount = (cart) => {
    if (!cart?.items || !Array.isArray(cart.items)) {
      return 0;
    }

    return cart.items.reduce((count, item) => {
      if (Number(item.variant_id) === DLN_FREE_GIFT_VARIANT_ID) {
        return count;
      }

      return count + Number(item.quantity || 0);
    }, 0);
  };

  /**
   * Fetches the current Shopify cart and renders the count
   * without counting the DLN free gift.
   *
   * @param {boolean} [animate=true]
   */
  refreshDLNCartCount = async (animate = true) => {
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) return;

      const cart = await response.json();
      const itemCount = this.getDLNCartCount(cart);

      await this.renderCartBubble(itemCount, animate);
    } catch (error) {
      console.warn('[DLN cart-icon] Could not refresh cart count:', error);
    }
  };

  /**
   * Handles the page show event when the page is restored from cache.
   *
   * @param {PageTransitionEvent} event
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.refreshDLNCartCount(false);
    }
  };

  /**
   * Handles cart updates.
   *
   * Horizon's event quantity includes the free gift, so DLN deliberately
   * reads /cart.js instead and calculates the visible quantity itself.
   *
   * @param {CartLinesUpdateEvent} event
   */
  onCartUpdate = (event) => {
    event.promise
      ?.then(() => {
        this.refreshDLNCartCount(true);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[cart-icon] Event promise rejected:', error);
        }
      });
  };

  /**
   * Renders the cart bubble.
   *
   * @param {number} itemCount
   * @param {boolean} [animate=true]
   */
  renderCartBubble = async (itemCount, animate = true) => {
    const isEmpty = itemCount === 0;

    this.refs.cartBubbleCount.classList.toggle('hidden', isEmpty);
    this.refs.cartBubble.classList.toggle('visually-hidden', isEmpty);

    this.currentCartCount = itemCount;

    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      itemCount > 0
    );

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || isEmpty) return;

    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');

    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count stored in sessionStorage matches
   * the currently rendered quantity.
   */
  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (value === visibleCount) return;

      if (Date.now() - timestamp < 3000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}